import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { agents } from '../../../../../../../api/agents'
import { createAttachmentTracker, getAgentConfig } from '../agentConfigs'

function stampMessages (messages, timestampMap) {
  let changed = false
  for (let i = 0; i < messages.length; i++) {
    if (!timestampMap.has(i)) {
      timestampMap.set(i, new Date().toISOString())
      changed = true
    }
  }
  if (!changed) return null
  return messages.map((m, i) => ({ ...m, createdAt: m.createdAt || timestampMap.get(i) }))
}

function getInFlightToolMessage (messages, toolMessages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role !== 'assistant' || !Array.isArray(message.toolCalls) || message.toolCalls.length === 0) {
      continue
    }

    for (const toolCall of message.toolCalls.slice().reverse()) {
      const hasToolResult = messages.slice(i + 1).some(nextMessage => (
        nextMessage?.role === 'tool' && (
          (toolCall.id && nextMessage.toolCallId === toolCall.id) ||
          (toolCall.name && nextMessage.name === toolCall.name)
        )
      ))

      if (!hasToolResult) {
        return toolMessages[toolCall.name] || `Running ${toolCall.name}...`
      }
    }
  }

  return null
}

export function useAgentPanel (agentId, options = {}) {
  const { workspaceContext } = options
  const config = getAgentConfig(agentId)
  const attachmentTracker = useMemo(() => createAttachmentTracker(), [])

  const [threadId, setThreadId] = useState(null)
  const [pendingSend, setPendingSend] = useState(false)
  const [optimisticMessage, setOptimisticMessage] = useState(null)
  const messageCountAtSend = useRef(null)
  const serverMessagesRef = useRef([])
  const timestampMap = useRef(new Map())

  const { mutateAsync: invokeAgent, isError: invokeError, isLoading: isInvoking } = agents.useInvokeAgent(config.agentId)
  const { mutateAsync: resumeAgentMutation, isLoading: isResuming } = agents.useResumeAgent(config.agentId)
  const { data: threadData, isLoading: isThreadLoading, isFetching: isThreadFetching } = agents.useAgentThread(threadId, {
    enabled: !!threadId,
    agentId: config.agentId
  })

  const status = threadData?.status
  const serverMessages = useMemo(() => threadData?.messages || [], [threadData?.messages])
  serverMessagesRef.current = serverMessages
  const interrupts = threadData?.interrupts || []
  const isPendingAction = status === 'pending_action'

  useEffect(() => {
    if (!pendingSend) return
    const threadIsProcessing = status === 'pending' || status === 'running'
    const messagesIncreased = messageCountAtSend.current !== null && serverMessages.length > messageCountAtSend.current
    if (threadIsProcessing || messagesIncreased) {
      setPendingSend(false)
      messageCountAtSend.current = null
    }
  }, [pendingSend, status, serverMessages.length])

  const serverHasOptimistic = optimisticMessage && serverMessages.some(m => {
    if (m.role !== 'user') return false
    let text = ''
    if (typeof m.content === 'string') {
      text = m.content
    } else if (Array.isArray(m.content)) {
      text = m.content.find(c => c.type === 'text')?.text || ''
    }
    return text === optimisticMessage.content
  })

  useEffect(() => {
    if (serverHasOptimistic) {
      setOptimisticMessage(null)
    }
  }, [serverHasOptimistic])

  const messages = useMemo(() => {
    const raw = optimisticMessage && !serverHasOptimistic
      ? [...serverMessages, optimisticMessage]
      : serverMessages
    return stampMessages(raw, timestampMap.current) || raw.map((m, i) => ({
      ...m,
      createdAt: m.createdAt || timestampMap.current.get(i)
    }))
  }, [serverMessages, optimisticMessage, serverHasOptimistic])

  const addMessage = useCallback(async (message) => {
    const attachments = attachmentTracker.buildAttachments(workspaceContext)
    messageCountAtSend.current = serverMessagesRef.current.length
    setPendingSend(true)
    setOptimisticMessage({ role: 'user', content: message, createdAt: new Date().toISOString() })

    try {
      const result = await invokeAgent({
        message,
        threadId: threadId || undefined,
        ...(attachments.length > 0 ? { attachments } : {})
      })
      if (!threadId && result?.threadId) {
        setThreadId(result.threadId)
      }
      return result
    } catch (error) {
      setPendingSend(false)
      setOptimisticMessage(null)
      throw error
    }
  }, [invokeAgent, threadId, workspaceContext, attachmentTracker])

  const resumeThread = useCallback(async (action, feedback) => {
    if (!threadId) return
    return resumeAgentMutation({ threadId, action, feedback: feedback || undefined })
  }, [threadId, resumeAgentMutation])

  const isProcessing = status === 'pending' || status === 'running' || isInvoking || pendingSend
  const hasMessages = messages.length > 0
  const isLoadingState = isInvoking || pendingSend || (!hasMessages && threadId && (isThreadLoading || isThreadFetching))

  const processingMessage = isPendingAction
    ? null
    : getInFlightToolMessage(messages, config.toolMessages) ||
      (status === 'pending' ? 'Queued request...' : null) ||
      (isProcessing ? 'Thinking...' : null)

  return {
    addMessage,
    resumeThread,
    threadId,
    threadData,
    messages,
    agentStatus: isPendingAction ? 'pending_action' : (isProcessing ? 'processing' : (status || 'idle')),
    isPendingAction,
    isResuming,
    interrupts,
    processingMessage,
    isThreadLoading: isLoadingState,
    isErrored: invokeError || status === 'error',
    errorMessage: threadData?.errorMessage || null,
    config,
    workspaceContext
  }
}
